import { useCurrentAccount } from "@/hooks/use-current-account";
import { isNewUiResolutionLoading } from "@/hooks/new-ui-resolution";
import { usePostHogFeatureFlag } from "@/hooks/use-posthog-feature-flag";
import { isOfficialTestAccount, useTestUiMode } from "@/lib/test-account-environment";

/**
 * Flag PostHog do teste A/B da UI nova para contas legacy.
 * NMG não passa pela flag — recebe a UI nova nativamente (100%).
 *
 * ⚠️ INVARIANTE DE SINCRONIA (Manager ↔ App) — LEIA ANTES DE MEXER:
 * A coorte deste flag define o "novo sistema" e DEVE permanecer idêntica à
 * dos 5 flags do app (`nova-navbar`, `trilhas`, `check-in-diario`,
 * `mural-escola`, `agenda-e-news`): mesmos condition sets no PostHog
 * (allowlist de world_id + account_created_at >= 2026-06-01; os do app têm
 * ainda is_nmg = true). Quem recebe o design novo do Manager recebe JUNTO a
 * nova navbar e as features novas no app (newapp/pages/MainPro + routes.tsx).
 * Qualquer mudança de coorte (adicionar unidade, % de rollout, nova condição)
 * precisa ser aplicada NOS 6 FLAGS de uma vez.
 * Fonte canônica: readme/POSTHOG_MANAGER_APP_CONTEXT.md (§ coorte unificada).
 */
export const NEW_UI_FLAG_KEY = "manager-new-ui-ab";

export type NewUiState = {
  /** Conta enxerga a UI nova: NMG nativo OU coorte de tratamento do A/B. */
  isNewUi: boolean;
  /** Coorte real da conta, sem o override local de QA. */
  cohortNewUi: boolean;
  /** Conta legacy sorteada para o tratamento (para telemetria/condicionais finas). */
  isAbTreatment: boolean;
  /** Flag do PostHog ainda não resolveu — só ocorre para conta legacy. */
  isLoading: boolean;
};

/**
 * Chave de modo de UI do Manager (shell, sidebar, guards de rota, redirects).
 * NUNCA usar `isNewManagerGlobal` direto para decidir modo de UI — usar este hook,
 * que soma a coorte do A/B. Billing, limites e gates de plano continuam decididos
 * por `isNewManagerGlobal`/`useCurrentPlan`.
 */
export function useNewUi(): NewUiState {
  const account = useCurrentAccount();
  const isNewManagerGlobal = Boolean(account?.["new-manager-global"]);
  // undefined enquanto o PostHog não resolveu a flag nesta sessão.
  const flag = usePostHogFeatureFlag(NEW_UI_FLAG_KEY);
  const isAbTreatment = !isNewManagerGlobal && flag === true;
  const cohortNewUi = isNewManagerGlobal || isAbTreatment;
  // Conta teste: seletor de ambiente da sidebar pode forçar novo/antigo design
  // (localStorage; ver lib/test-account-environment.ts).
  const [testUiMode] = useTestUiMode();
  const isOfficialTestWorld = isOfficialTestAccount(account);

  if (isOfficialTestWorld && testUiMode !== "auto") {
    return {
      isNewUi: testUiMode === "new",
      cohortNewUi,
      isAbTreatment: false,
      isLoading: false,
    };
  }

  return {
    isNewUi: cohortNewUi,
    cohortNewUi,
    isAbTreatment,
    isLoading: isNewUiResolutionLoading(account, flag),
  };
}
